import React, { useEffect, useState } from "react";
import { gql, useSubscription } from "@apollo/client";
import {
  TimeSubscriptionTest_TimeSubscription,
  TimeSubscriptionTest_TimeSubscriptionVariables,
} from "../__generated__/graphql";

const TimeSubscriptionTest_Time = gql`
  subscription TimeSubscriptionTest_Time($testText: String!) {
    time(testText: $testText) {
      testText
    }
  }
`;

const TimeSubscriptionTest: React.FC = () => {
  const [currentTime, setCurrentTime] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [wsUrl, setWsUrl] = useState<string>("");

  // Generate WebSocket URL for display
  useEffect(() => {
    const url = window.location.origin
      .replace("http://", "ws://")
      .replace("https://", "wss://")
      .concat("/subscriptions");
    setWsUrl(url);
    addLog(`WebSocket URL: ${url}`);
  }, []);

  // Helper to add logs with timestamps
  const addLog = (message: string) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] ${message}`;
    setLogs((prev) => [logMessage, ...prev].slice(0, 20));
    console.log(logMessage);
  };

  // Set up the subscription using useSubscription hook
  const { data, loading } = useSubscription<
    TimeSubscriptionTest_TimeSubscription,
    TimeSubscriptionTest_TimeSubscriptionVariables
  >(TimeSubscriptionTest_Time, {
    shouldResubscribe: true,
    variables: {
      testText: "yoshi testing",
    },
    onSubscriptionData: (options) => {
      const time = options.subscriptionData.data?.time;
      if (time) {
        addLog(`Processing time: ${time.testText}`);
        setCurrentTime(time.testText);
        setIsConnected(true);
      }
    },
    onSubscriptionComplete: () => {
      addLog("Subscription completed");
      setIsConnected(false);
    },
    onError: (err) => {
      addLog(`Subscription error: ${err.message}`);
      setError(err);
      setIsConnected(false);
    },
  });

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">WebSocket Subscription Test</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded p-4">
          <h3 className="font-semibold text-lg mb-3">Connection Info</h3>

          <div className="mb-2">
            <span className="font-semibold">WebSocket URL:</span>
            <span className="ml-2 font-mono text-sm break-all">{wsUrl}</span>
          </div>

          <div className="mb-2">
            <span className="font-semibold">Status:</span>
            {loading ? (
              <span className="ml-2 text-yellow-600 font-semibold">
                Setting up...
              </span>
            ) : isConnected ? (
              <span className="ml-2 text-green-600 font-semibold">
                Connected
              </span>
            ) : error ? (
              <span className="ml-2 text-red-600 font-semibold">Error</span>
            ) : (
              <span className="ml-2 text-gray-600 font-semibold">Idle</span>
            )}
          </div>

          {currentTime && (
            <div className="mb-2">
              <span className="font-semibold">Current time:</span>
              <span className="ml-2">
                {new Date(currentTime).toLocaleTimeString()}
              </span>
            </div>
          )}

          <div className="mt-4">
            <span className="font-semibold">Raw Data:</span>
            <pre className="mt-1 p-2 bg-gray-100 text-xs rounded overflow-auto">
              {data ? JSON.stringify(data, null, 2) : "No data"}
            </pre>
          </div>
        </div>

        <div className="bg-gray-50 rounded p-4">
          <h3 className="font-semibold text-lg mb-3">Debug Logs</h3>
          <div className="overflow-auto max-h-60 bg-gray-100 p-2 rounded text-xs font-mono">
            {logs.map((log, i) => (
              <div key={i} className="mb-1">
                {log}
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded mt-6">
          <h3 className="font-semibold mb-2">Error Details</h3>
          <p>{error.message}</p>
          <details className="mt-2">
            <summary className="cursor-pointer">Stack trace</summary>
            <pre className="text-xs mt-2 p-2 bg-gray-100 overflow-auto">
              {error.stack}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default TimeSubscriptionTest;
